import { View, Text } from "@react-pdf/renderer";
import type { FC } from "react";
import type { TableBlock } from "@/types/blocks";
import styles from "./pdfStyles";
import { fontSize, spacing } from "./pdfTokens";
import { formatCompactNumber } from "@/utils/formatCompactNumber";

interface PdfTableProps {
  block: TableBlock;
}

function isCompact(val: string): boolean {
  const t = val.trim();
  if (!t) return false;
  return /^[\d,.$%€£¥+\-–() KMBTkmbt]+$/.test(t);
}

function columnIsCompact(ci: number, rows: string[][]): boolean {
  if (rows.length === 0) return false;
  return rows.every((row) => isCompact(row[ci] ?? ""));
}

function longestWord(text: string): number {
  return text.split(/[\s/]+/).reduce((max, w) => Math.max(max, w.length), 0);
}

function computeColumnWidths(columns: string[], rows: string[][]): string[] {
  const n = columns.length;

  const weights = columns.map((col, ci) => {
    const headerLen = col.length;
    const maxCellLen = rows.reduce((max, row) => Math.max(max, (row[ci] ?? "").length), 0);
    const maxWordLen = rows.reduce(
      (max, row) => Math.max(max, longestWord(row[ci] ?? "")),
      longestWord(col),
    );
    const contentLen = Math.max(headerLen, maxCellLen, 4);

    if (columnIsCompact(ci, rows)) {
      return Math.max(maxWordLen * 1.4, contentLen * 0.55);
    }

    const avgCellLen =
      rows.length > 0
        ? rows.reduce((sum, row) => sum + (row[ci] ?? "").length, 0) / rows.length
        : headerLen;

    return Math.max(contentLen, avgCellLen * 1.1, headerLen * 1.3);
  });

  const totalWeight = weights.reduce((sum, w) => sum + w, 0);
  const evenPct = 100 / n;
  const minPct = Math.max(evenPct * 0.6, 8);

  const rawWidths = weights.map((w) => Math.max(minPct, (w / totalWeight) * 100));
  const rawTotal = rawWidths.reduce((sum, w) => sum + w, 0);
  return rawWidths.map((w) => `${((w / rawTotal) * 100).toFixed(2)}%`);
}

const MIN_CELL_PAD = 6;

export const PdfTable: FC<PdfTableProps> = ({ block }) => {
  const displayRows = block.rows.map((row) => row.map((cell) => formatCompactNumber(cell)));
  const colCount = block.columns.length;
  const colWidths = computeColumnWidths(block.columns, displayRows);

  const cellPadH = colCount >= 7 ? MIN_CELL_PAD : colCount >= 5 ? spacing.sm : spacing.md;
  const cellFontSize = colCount >= 7 ? 7 : colCount >= 6 ? fontSize.xs : fontSize.sm;

  return (
    <View>
      {block.title && <Text style={styles.blockTitle}>{block.title}</Text>}
      <View style={styles.tableContainer}>
        <View style={styles.tableHeaderRow} wrap={false} fixed>
          {block.columns.map((col, i) => (
            <View key={i} style={{ width: colWidths[i] }}>
              <Text
                style={[
                  styles.tableHeaderCell,
                  { paddingHorizontal: cellPadH, fontSize: cellFontSize },
                ]}
              >
                {col}
              </Text>
            </View>
          ))}
        </View>
        {displayRows.map((row, ri) => (
          <View
            key={ri}
            style={[styles.tableRow, ri % 2 === 1 ? styles.tableRowEven : {}]}
            wrap={false}
          >
            {row.map((cell, ci) => (
              <View key={ci} style={{ width: colWidths[ci] }}>
                <Text
                  style={[
                    styles.tableCell,
                    { paddingHorizontal: cellPadH, fontSize: cellFontSize },
                  ]}
                >
                  {cell}
                </Text>
              </View>
            ))}
          </View>
        ))}
      </View>
    </View>
  );
};
